<script lang="ts">
  // NPC conversation, a visual-novel scene like the real Yu-Gi-Oh! games: the
  // character stands prominently on a darkened cyber stage, with a dialog band
  // pinned along the bottom (nameplate + typed line + advance / duel controls).
  // Material is the VRAINS data-window vocabulary: chamfered neon panel, cyan
  // edge + glow, hex-grid bleed. Click anywhere (or Space/Enter) to advance; the
  // first click completes the typing line, the next advances.
  import type { NpcBust } from "@ygoevo/yo2-data";
  import type { WorldNpc } from "../world/npcs.ts";
  import NpcBustView from "./NpcBustView.svelte";
  import { createHotkey } from "../hotkeys.ts";

  let {
    npc,
    bust,
    onClose,
    onDuel,
  }: {
    npc: WorldNpc;
    bust: NpcBust | null;
    onClose: () => void;
    onDuel: (npc: WorldNpc) => void;
  } = $props();

  let step = $state(0);
  let innerH = $state(800);
  const last = $derived(step >= npc.lines.length - 1);
  const line = $derived(npc.lines[Math.min(step, npc.lines.length - 1)] ?? "");

  // Bust fills most of the height and rises from behind the dialog band.
  const bustH = $derived(Math.round(Math.min(innerH * 0.9, 880)));

  const reduce =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ── typewriter ───────────────────────────────────────────────────────────
  let shown = $state("");
  let typing = $state(false);
  $effect(() => {
    const full = line;
    if (reduce) { shown = full; typing = false; return; }
    shown = "";
    typing = true;
    let i = 0;
    const id = setInterval(() => {
      i++;
      shown = full.slice(0, i);
      if (i >= full.length) { typing = false; clearInterval(id); }
    }, 22);
    return () => clearInterval(id);
  });

  function advance() {
    if (typing) { shown = line; typing = false; return; } // first click: finish the line
    if (last) onClose();
    else step += 1;
  }

  // Space / Enter advance the conversation (ignored while typing in a field by
  // default; `allow` lets these coexist with the world's own Enter shortcut).
  createHotkey("Space", advance, { conflictBehavior: "allow" });
  createHotkey("Enter", advance, { conflictBehavior: "allow" });

  function duel(e: MouseEvent) { e.stopPropagation(); onDuel(npc); }
  function close(e: MouseEvent) { e.stopPropagation(); onClose(); }
</script>

<svelte:window bind:innerHeight={innerH} />

<!-- Full-screen VN stage. The whole stage advances on click; explicit buttons stop propagation. -->
<div
  class="tp-npc vn fixed inset-0 z-50 overflow-hidden"
  role="presentation"
  onclick={advance}
>
  <!-- Light, blur-free scrim keeps the playable world readable behind the scene. -->
  <div class="modal-scrim tp-npc-scrim absolute inset-0 bg-void/50"></div>
  <div class="vn-vignette absolute inset-0"></div>
  <div class="grid-tex absolute inset-0 opacity-40"></div>

  <!-- character spotlight + bust, rising from behind the dialog band -->
  <div class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center">
    <div class="vn-spot absolute bottom-0 left-1/2 -translate-x-1/2"></div>
    <div class="vn-bust relative" class:vn-bust-anim={!reduce}>
      {#if bust}
        <NpcBustView {bust} height={bustH} />
      {/if}
      <div class="vn-ground absolute inset-x-0 bottom-0"></div>
    </div>
  </div>

  <!-- close (walk away) -->
  <button
    class="vn-x chamfer-btn absolute right-4 top-4 z-10 grid size-9 place-items-center bg-surface-2/80 text-ink-dim transition-colors hover:text-magenta hover:glow-magenta"
    style="--c:7px"
    onclick={close}
    aria-label="Walk away"
  >
    <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
  </button>

  <!-- dialog band -->
  <div class="absolute inset-x-0 bottom-0 flex justify-center px-3 pb-3 sm:px-6 sm:pb-6">
    <div class="world-vn-panel relative w-full max-w-[1080px]">
      <!-- nameplate tab, raised over the panel's top edge -->
      <div class="tp-npc-name vn-name chamfer absolute -top-4 left-5 z-10 bg-cyan px-4 py-1 glow-cyan" style="--c:8px">
        <span class="font-display text-base font-bold uppercase leading-none tracking-[0.08em] text-void">
          {npc.name}
        </span>
        <span class="ml-2 font-display text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-void/70">
          {npc.title}
        </span>
      </div>

      <!-- panel -->
      <div class="chamfer bg-cyan/70 glow-cyan p-px" style="--c:16px">
        <div class="tp-npc-panel chamfer grid-tex relative bg-surface/95 px-6 pb-5 pt-7 sm:px-8" style="--c:15px">
          <div
            class="pointer-events-none absolute inset-x-8 top-0 h-px"
            style="background:linear-gradient(90deg,transparent,color-mix(in oklab,var(--color-cyan) 80%,transparent),transparent)"
          ></div>

          <!-- spoken line -->
          <p class="tp-npc-line min-h-[3.4em] max-w-[62ch] font-body text-[1.05rem] leading-relaxed text-ink sm:text-[1.15rem]">
            {shown}<span class="vn-caret" class:vn-caret-on={typing}></span>
          </p>

          <!-- footer: progress · continue ▼ · actions -->
          <div class="mt-3 flex items-end justify-between gap-4">
            <div class="flex items-center gap-1.5">
              {#each npc.lines as _, i (i)}
                <span class="h-1 rounded-full transition-all duration-200 {i === step ? 'w-5 bg-cyan' : i < step ? 'w-2.5 bg-cyan/45' : 'w-2.5 bg-ink-dim/25'}"></span>
              {/each}
            </div>

            <div class="flex items-center gap-2.5">
              {#if !typing && !last}
                <span class="vn-next animate-pulse-soft mr-1 font-display text-xs uppercase tracking-[0.2em] text-cyan/70">
                  <span class="touch:hidden">click to continue ▾</span><span class="hidden touch:inline">tap to continue ▾</span>
                </span>
              {/if}
              {#if last && npc.canDuel}
                <button
                  onclick={duel}
                  class="vn-btn chamfer-btn flex items-center gap-2 bg-magenta/15 px-6 py-2.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-magenta transition-colors hover:bg-magenta/25 hover:glow-magenta"
                  style="--c:9px"
                >
                  <svg viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/><line x1="13" x2="19" y1="19" y2="13"/><line x1="16" x2="20" y1="16" y2="20"/><line x1="19" x2="21" y1="21" y2="19"/><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/><line x1="5" x2="9" y1="14" y2="18"/></svg>
                  Duel
                </button>
              {/if}
              <button
                onclick={(e) => { e.stopPropagation(); advance(); }}
                class="vn-btn chamfer-btn bg-surface-2 px-6 py-2.5 font-display text-sm font-semibold uppercase tracking-[0.12em] text-ink transition-colors hover:text-cyan hover:glow-cyan"
                style="--c:9px"
              >
                {last ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  /* Visible by default; the fade is a non-gating enhancement (no fill → at rest
     the overlay is fully opaque, so it never ships blank if rAF is throttled). */
  .vn { animation: vn-in 200ms ease-out; }
  @keyframes vn-in { from { opacity: 0; } }

  /* Radial vignette: darken the edges so the lit character pops. */
  .vn-vignette {
    background: radial-gradient(120% 90% at 50% 38%, transparent 40%, color-mix(in oklab, var(--color-void) 92%, transparent) 100%);
  }
  /* Cyan spotlight cone behind the character. */
  .vn-spot {
    width: min(70vw, 720px);
    height: min(70vh, 720px);
    background: radial-gradient(closest-side, color-mix(in oklab, var(--color-cyan) 22%, transparent), transparent 72%);
    filter: blur(8px);
  }
  /* Elliptical ground glow at the character's feet. */
  .vn-ground {
    height: 38px;
    background: radial-gradient(closest-side, color-mix(in oklab, var(--color-cyan) 38%, transparent), transparent);
    filter: blur(3px);
  }
  .vn-bust { filter: drop-shadow(0 8px 24px color-mix(in oklab, var(--color-void) 80%, transparent)); }

  /* Entrance: rise (no fill → rest state is the visible default, so a throttled/
     headless render never freezes the bust invisible), then a gentle idle float. */
  .vn-bust-anim { animation: vn-rise 420ms cubic-bezier(0.16, 1, 0.3, 1), vn-float 6s ease-in-out 420ms infinite; }
  @keyframes vn-rise { from { opacity: 0; transform: translateY(28px); } }
  @keyframes vn-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

  /* Typing caret. */
  .vn-caret {
    display: inline-block;
    width: 0.5ch;
    height: 1.05em;
    margin-left: 1px;
    vertical-align: -0.12em;
    background: var(--color-cyan);
    opacity: 0;
  }
  .vn-caret-on { opacity: 1; animation: vn-blink 0.9s steps(1) infinite; }
  @keyframes vn-blink { 50% { opacity: 0; } }

  @media (prefers-reduced-motion: reduce) {
    .vn-bust-anim { animation: none; }
    .vn-caret-on { animation: none; }
  }
</style>
